import { useState } from "react";
import { useTranslation } from "react-i18next";
import Constants from "../../../../constants/Index";
import { generateuuid } from "../../../../utils/uuid/generate";
import { ShapefileUser } from "./draft";
import { useOnboarding } from "../../../../hooks/useOnboarding";
import { IProfile } from "../../../../@types/app/IProfile";
import { UserFormV2 } from "../../../FormsV2";
import { ButtonV2, DropdownV2, FormCardV2 } from "../../../FormControlsV2";
import { OnboardingFooterV2 } from "../../../OnboardingV2/OnboardingFooterV2";

export type ForemanDraft = {
  mode: "existing" | "new";
  selectedUserId: string;
  appliedUserId: string;
  name: string;
  phone: string;
  newUserId: string;
};

// Dropdown labels preserve the original product role, in both languages.
const foremanProfile = { label: "Capataz", value: Constants.PROFILE_CODE_CAPATAZ };
export const emptyForemanDraft: ForemanDraft = {
  mode: "existing", selectedUserId: "", appliedUserId: "", name: "", phone: "", newUserId: "",
};
const digits = (value: string) => value.replace(/\D/g, "");

export function ForemanStep({ farmId, draft, onChange }: {
  farmId: string;
  draft: ForemanDraft;
  onChange: (draft: ForemanDraft) => void;
}) {
  const { t } = useTranslation();
  const { payload, setPayload } = useOnboarding();
  const [showValidation, setShowValidation] = useState(false);
  const foremen = (payload.users || []).filter(user => Number(user.profile_id || user.profile?.value) === Constants.PROFILE_CODE_CAPATAZ);
  const options = foremen.map(user => ({ label: user.name, value: user.uuid }));
  const selected = options.find(option => option.value === draft.selectedUserId) || null;

  function update(partial: Partial<ForemanDraft>) {
    onChange({ ...draft, ...partial });
  }

  function validation() {
    const errors: { name?: string; phone?: string; selected?: string } = {};
    if (draft.mode === "existing") {
      if (!selected) errors.selected = t("shapefileForeman.selectRequired");
      return errors;
    }
    if (!draft.name) errors.name = t("internalValidation.required");
    else if (!/^[^\s]+(\s+[^\s]+)*$/.test(draft.name)) errors.name = t("internalValidation.validName");
    else if (draft.name.length < 3) errors.name = t("internalValidation.minName");
    if (!draft.phone) errors.phone = t("internalValidation.required");
    else if (digits(draft.phone).length !== 11) errors.phone = t("internalValidation.invalidPhone");
    else if (payload.users.some(user => user.uuid !== draft.newUserId && digits(user.phone || "") === digits(draft.phone))) {
      errors.phone = t("shapefileForeman.phoneInUse");
    }
    return errors;
  }

  function applyForeman(user: IProfile, users = payload.users) {
    setPayload(current => ({
      ...current,
      users,
      areas: (current.areas || []).map(area => {
        const manager = area.manager_user_id;
        const managerId = manager && typeof manager === "object" ? manager.value : manager;
        if (String(area.farm_id) !== farmId || (managerId && String(managerId) !== draft.appliedUserId)) return area;
        return { ...area, manager_user_id: { label: user.name, value: user.uuid } };
      }),
    }));
    onChange({ ...draft, selectedUserId: user.uuid, appliedUserId: user.uuid, ...(draft.mode === "new" ? { newUserId: user.uuid } : {}) });
  }

  function handleNext() {
    setShowValidation(true);
    if (Object.keys(validation()).length) return false;
    if (draft.mode === "existing") {
      const user = foremen.find(user => user.uuid === draft.selectedUserId);
      if (!user) return false;
      applyForeman(user);
      return true;
    }

    const user: ShapefileUser = {
      uuid: draft.newUserId || generateuuid(),
      name: draft.name,
      phone: digits(draft.phone),
      profile_id: Constants.PROFILE_CODE_CAPATAZ,
      profile: foremanProfile,
      isNewForShapefile: true,
    };
    applyForeman(user, [...payload.users.filter(item => item.uuid !== user.uuid), user]);
    return true;
  }

  const errors = showValidation ? validation() : {};
  return (
    <>
      <main>
        <div className="onboarding-v2-form-page shapefile-foreman">
          <FormCardV2 title={t("shapefileForeman.title")} description={t("shapefileForeman.help")}>
            <div className="shapefile-foreman__choices">
              <ButtonV2 variant={draft.mode === "existing" ? "primary" : "secondary"} onClick={() => update({ mode: "existing" })}>
                {t("shapefileForeman.useExisting")}
              </ButtonV2>
              <ButtonV2 variant={draft.mode === "new" ? "primary" : "secondary"} onClick={() => update({ mode: "new" })}>
                {t("shapefileForeman.createNew")}
              </ButtonV2>
            </div>
            {draft.mode === "existing" ? (
              <DropdownV2
                name="foreman.existing"
                label={t("shapefileForeman.title")}
                placeholder={t("shapefileForeman.select")}
                emptyMessage={t("shapefileForeman.empty")}
                options={options}
                value={selected}
                searchable
                error={errors.selected}
                onChange={option => update({ selectedUserId: String(option?.value || "") })}
              />
            ) : (
              <>
                <UserFormV2
                  namePrefix="foreman.new"
                  profile={foremanProfile}
                  profiles={[foremanProfile]}
                  profileDisabled
                  name={draft.name}
                  phone={draft.phone}
                  errors={errors}
                  onProfileChange={() => {}}
                  onNameChange={name => update({ name })}
                  onPhoneChange={phone => update({ phone })}
                />
              </>
            )}
          </FormCardV2>
        </div>
      </main>
      <OnboardingFooterV2 nextStepEvent={handleNext} />
    </>
  );
}
